import type { PermissionRequest, QuestionRequest } from "@mux/sdk/v2/client"
import { batch } from "solid-js"
import { Binary } from "@/utils/binary"
import { produce, reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { askedRequestRoute, decodeRequestRoute, REQUEST_ROUTE_EVENT } from "@/transport/requests"
import type { GlobalStore } from "./types"

/** Applies where a chat's open requests now surface; false for any other event. */
export function applyRequestRouteEvent(input: {
  event: { type: string; properties?: unknown }
  setStore: SetStoreFunction<GlobalStore>
}) {
  if (input.event.type !== REQUEST_ROUTE_EVENT) return false
  try {
    const route = decodeRequestRoute(input.event.properties)
    input.setStore("request_route", route.sessionID, reconcile(route))
  } catch (error) {
    console.warn("Ignored an invalid request route", error)
  }
  return true
}

/**
 * Applies an asked or settled question or permission ask. A request new to
 * the store brings its chat's route; a replay of one already held does not,
 * since a later route may have replaced it. A chat left with nothing open
 * drops its route, since its next request brings one again.
 */
export function applyRequestEvent(input: {
  event: { type: string; properties?: unknown }
  store: Store<GlobalStore>
  setStore: SetStoreFunction<GlobalStore>
}) {
  const event = input.event
  if (event.type === "permission.asked") {
    const request = event.properties as PermissionRequest
    batch(() => {
      const requests = input.store.permission[request.sessionID]
      if (!requests) {
        applyAskedRoute(input.setStore, request)
        input.setStore("permission", request.sessionID, [request])
        return
      }
      const result = Binary.search(requests, request.id, (item) => item.id)
      if (result.found) {
        input.setStore("permission", request.sessionID, result.index, reconcile(request))
        return
      }
      applyAskedRoute(input.setStore, request)
      input.setStore("permission", request.sessionID, produce((draft) => draft.splice(result.index, 0, request)))
    })
    return true
  }
  if (event.type === "question.asked") {
    const request = event.properties as QuestionRequest
    batch(() => {
      const requests = input.store.question[request.sessionID]
      if (!requests) {
        applyAskedRoute(input.setStore, request)
        input.setStore("question", request.sessionID, [request])
        return
      }
      const result = Binary.search(requests, request.id, (item) => item.id)
      if (result.found) {
        input.setStore("question", request.sessionID, result.index, reconcile(request))
        return
      }
      applyAskedRoute(input.setStore, request)
      input.setStore("question", request.sessionID, produce((draft) => draft.splice(result.index, 0, request)))
    })
    return true
  }
  if (event.type !== "permission.replied" && event.type !== "question.replied" && event.type !== "question.rejected") {
    return false
  }
  const props = event.properties as { sessionID: string; requestID: string }
  batch(() => {
    if (event.type === "permission.replied") {
      const requests = input.store.permission[props.sessionID] ?? []
      const index = requests.findIndex((request) => request.id === props.requestID)
      if (index === -1) return
      input.setStore("permission", props.sessionID, produce((draft) => draft.splice(index, 1)))
    } else {
      const requests = input.store.question[props.sessionID] ?? []
      const index = requests.findIndex((request) => request.id === props.requestID)
      if (index === -1) return
      input.setStore("question", props.sessionID, produce((draft) => draft.splice(index, 1)))
    }
    if (!input.store.question[props.sessionID]?.length && !input.store.permission[props.sessionID]?.length) {
      input.setStore("request_route", props.sessionID, undefined)
    }
  })
  return true
}

function applyAskedRoute(setStore: SetStoreFunction<GlobalStore>, request: PermissionRequest | QuestionRequest) {
  try {
    const route = askedRequestRoute(request)
    if (route) setStore("request_route", route.sessionID, reconcile(route))
  } catch (error) {
    console.warn("Ignored an invalid request route", error)
  }
}
